import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, useParams } from 'react-router';

import { createNewEventSession, updateHomepageMainNav } from '../../../../../../store/actions/admin/create-event';
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { GetDefaultMainNavItems } from '../../../../../../store/utils/create-event';
import { CreateSession, NavMainItems, Session, SessionTypesEnum } from '../../../../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import { PATHNAMES } from '../../../../admin';
import SessionModal from '../../../session/session-modal';
import SessionList from '../sessions/session-list';
import SortableSessionNavItem from './event/sortable-nav-item';
import { EMPTY_FILTER_STATE } from './event/submenus/sessions-panel';
import { updateTranslateKey } from '../../../../../../utils/utils';
import { getHomepageDefaultLanguage } from '../../../../../../utils/get-language';
import { ParamsProps } from '../../../../../live-event/live-event';
import './single-session-pages.scss';

interface ISingleSessionPages {
	sessions: Session[];
}

// this panel state only exists for single session events
const SingleSessionPages: React.FC<ISingleSessionPages> = ({ sessions }) => {
	const {
		workingEvent,
		auth: { user, token }
	} = useTypedSelector(state => ({
		workingEvent: state.CreateEventReducer.workingEvent,
		auth: state.AuthReducer
	}));

	const [addingSession, setAddingSession] = useState<SessionTypesEnum | null>(null);
	const { id: eventUuid } = useParams<{ id: string; }>();

	const { language } = useParams() as ParamsProps;
	const dispatch = useDispatch();

	const navItems = useMemo(() => {
		return workingEvent?.homepage?.event_main_nav_items || GetDefaultMainNavItems(true);
	}, [workingEvent?.homepage?.event_main_nav_items]);
	const homepageNavItem = navItems.find(item => item.name === NavMainItems.Home);

	const handleUpdateTitle = (value: string) => {
		const _mainNavItems = navItems.map(ni => {
			if (ni.name === NavMainItems.Home) {
				return {
					...ni,
					custom_name: updateTranslateKey({
						translateString: homepageNavItem!.custom_name,
						input: value,
						language,
						baseLanguage: getHomepageDefaultLanguage()
					}),
				};
			}
			return ni;
		});
		dispatch(updateHomepageMainNav(_mainNavItems));
	};

	function createSession(session: CreateSession) {
		return new Promise(resolve => {
			if (!user || !workingEvent || !token) return;
			//if the user didn't pick a language, they get english
			if (session.languages.length === 0) {
				session.languages = ['en'];
				session.default_language = 'en';
			}

			//if there's no default language, make it english if it exists, otherwise pick the first non-english language
			// or if the default language is not in the list
			if (!session.default_language || !session.languages.includes(session.default_language)) {
				if (session.languages.includes('en')) {
					session.default_language = 'en';
				} else {
					session.default_language = session.languages[0];
				}
			}
			// move all updates to a redux thunk action creator so we can easily await each call as promises
			dispatch(createNewEventSession({
				session,
				workingEvent,
				active_channel: user.active_channel,
				token,
				mulitpleSessions: 'multi',
				user,
			}, () => {
				setAddingSession(null);
				resolve({});
			}));
		});
	}

	if (eventUuid !== workingEvent?.uuid) return <Redirect to={PATHNAMES.Events} />;
	return (
		<div className="single-session-pages-panel-body">
			{workingEvent?.homepage && homepageNavItem && (

				<SortableSessionNavItem
					className="home-panel-card"
					navItem={homepageNavItem}
					workingEvent={workingEvent}
					id={homepageNavItem.name}
					handleUpdateTitle={handleUpdateTitle}
					handleViewerClick={() => null}
					notSortable
				/>
			)}
			<SessionList
				sessions={sessions}
				filters={EMPTY_FILTER_STATE}
				handleResetFilters={() => ("no filters here")}
			/>
			<div className="single-session-event-options">
				<button className="no-style edit-option" onClick={() => setAddingSession(SessionTypesEnum.onDemand)}>
					<Icon name={ICONS.ADD} size={16} color={COLORS.CYAN} />
					Add session
				</button>
			</div>
			<SessionModal
				addingSession={!!addingSession}
				addingSessionType={addingSession || undefined}
				closeModal={() => setAddingSession(null)}
				createSession={createSession}
			/>
		</div>
	);
};

export default SingleSessionPages;
